import React from 'react';
import { Helmet } from 'react-helmet-async';

import { APP_CONFIG } from '@/config';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'product';
  noIndex?: boolean;
  noFollow?: boolean;
  keywords?: string;
  jsonLd?: Record<string, unknown>;
}

const BASE_URL = APP_CONFIG.url.endsWith('/') ? APP_CONFIG.url.slice(0, -1) : APP_CONFIG.url;

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonical,
  ogImage = '/og-image.png',
  ogType = 'website',
  noIndex = false,
  noFollow = false,
  keywords,
  jsonLd,
}) => {
  const fullTitle = `${title} | ${APP_CONFIG.name}`;
  const canonicalUrl = canonical
    ? `${BASE_URL}${canonical.startsWith('/') ? canonical : `/${canonical}`}`
    : BASE_URL;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />

      <meta
        name="robots"
        content={`${noIndex ? 'noindex' : 'index'}, ${noFollow ? 'nofollow' : 'follow'}`}
      />

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={APP_CONFIG.name} />
      <meta
        property="og:image"
        content={`${BASE_URL}${ogImage.startsWith('/') ? ogImage : `/${ogImage}`}`}
      />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_IN" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta
        name="twitter:image"
        content={`${BASE_URL}${ogImage.startsWith('/') ? ogImage : `/${ogImage}`}`}
      />
      <meta name="twitter:site" content="@bstsoftstudio" />

      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
};

export default React.memo(SEO);
